import { Typography } from '@plato/ui/primitives/typography';
import { cn } from '@plato/ui/util/classes';
import { SearchIcon } from 'lucide-react';
import { useEffect, useState, type ComponentPropsWithRef, type ReactElement } from 'react';

const typingDotsIntervalMs = 400;

/** `.` / `..` / `...` — stepped dots marking an in-progress tool call. */
export function TypingDots(): ReactElement {
    const [step, setStep] = useState(0);
    useEffect(() => {
        const timer = window.setInterval(() => {
            setStep((value) => (value + 1) % 4);
        }, typingDotsIntervalMs);
        return () => {
            window.clearInterval(timer);
        };
    }, []);
    return <span aria-hidden>{'.'.repeat(step)}</span>;
}

export type ToolCallRowProps = ComponentPropsWithRef<'div'> & {
    /** The translated caption, shown as-is. */
    text: string;
    /** Renders the stepped dots while the invocation is still running. */
    inProgress?: boolean;
};

/** One caption line in the message flow for a tool invocation. */
export function ToolCallRow({ className, text, inProgress, ...props }: ToolCallRowProps): ReactElement {
    return (
        <div
            data-slot="tool-call-row"
            // Icon color matches the caption role's default.
            className={cn('flex items-center gap-tight self-start px-tight text-content-weaker', className)}
            {...props}
        >
            <SearchIcon className="size-3 shrink-0" />
            <Typography variant="caption">
                {text}
                {inProgress === true && <TypingDots />}
            </Typography>
        </div>
    );
}
